/**
 * Toast — lightweight inline notification strip.
 *
 * Self-dismisses after `durationMs` (default 4000). Respects
 * prefers-reduced-motion (no slide animation when set).
 *
 * Usage:
 *   const [toast, setToast] = useToast();
 *   setToast({ variant: 'success', message: 'Done!' });
 *   <Toast toast={toast} onDismiss={() => setToast(null)} />
 */

import { useEffect, useRef } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastState {
  variant: ToastVariant;
  message: string;
  durationMs?: number;
}

interface ToastProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
  error:   'bg-red-500/15 border-red-500/30 text-red-300',
  info:    'bg-violet-500/15 border-violet-500/30 text-violet-300',
};

export function Toast({ toast, onDismiss }: ToastProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!toast) return;
    const duration = toast.durationMs ?? 4000;
    timerRef.current = setTimeout(onDismiss, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`fixed bottom-6 right-6 z-60 max-w-sm px-4 py-3 rounded-xl border text-sm font-medium shadow-lg flex items-start gap-3 motion-safe:animate-in motion-safe:slide-in-from-bottom-2 ${VARIANT_CLASSES[toast.variant]}`}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
